import React from "react";
import {connect} from "react-redux";
import {Query} from "react-apollo";
import {fromJS, List} from "immutable";
import PanelGroupController from "../../../components/PanelGroupController";
import PropertiesList from "../../../components/panels/PropertiesList";
import InlineTable from "../../../components/InlineTable";
import ActionButton from "../../../components/ActionButton";
import Loading from "../../../components/Loading";
import GraphQLErrors from "../../../components/GraphQLErrors";
import StatusStepsPanel from "../../shared/StatusStepsPanel";
import {skuSummaryRows} from "../../shared/SkuSummary";
import {GET_CLIENT_DISPATCH} from "../../../queries/clientDispatches";
import {statusLabel, waitingFor} from "../statusDisplay";
import {
  requestEditClientDispatch,
  requestAddPalletToClientDispatch,
  requestAddManualPalletToClientDispatch,
  requestShipClientDispatch,
  requestAddSerialsToClientDispatch,
  requestSyncClientDispatchWithStord,
  requestCancelClientDispatch,
} from "../../../actions/ClientDispatchApiActions";

// The side panels of a client dispatch: its status, its details with the
// per-SKU summary, and what can be done with it. All read the same query as
// the details view, so they follow it.

const STEPS = [
  {id: "draft", title: "Draft"},
  {id: "shipped", title: "Shipped"},
];

const withDispatch = (clientDispatchId, render) => (
  <Query query={GET_CLIENT_DISPATCH} variables={{id: clientDispatchId}} fetchPolicy="cache-and-network">
    {(result) => render(result, result.data && result.data.clientDispatch)}
  </Query>
);

export const ClientDispatchStatusPanel = ({clientDispatchId}) =>
  withDispatch(clientDispatchId, (_result, dispatch) => (
    <StatusStepsPanel
      title="Dispatch Status"
      steps={STEPS}
      status={dispatch && dispatch.status}
      cancelledText={dispatch && dispatch.cancelReason ? `This dispatch was cancelled: ${dispatch.cancelReason}` : "This dispatch was cancelled."} />
  ));

const PROPERTIES = fromJS([
  {prop: "id", label: "Id", type: "id"},
  {prop: "statusLabel", label: "Status", type: "string"},
  {prop: "originWarehouseId", label: "From", type: "id"},
  {prop: "partnerName", label: "Client", type: "string"},
  {prop: "deliverTo", label: "Deliver To", type: "string"},
  {prop: "reference", label: "Client Reference", type: "string"},
  {prop: "carrierReference", label: "Carrier Reference", type: "string"},
  {prop: "stordOrderNumber", label: "Stord Order", type: "string"},
  {prop: "dispatchDate", label: "Dispatch Date", type: "string"},
  {prop: "shippedAt", label: "Shipped", type: "utc"},
  {prop: "cancelledAt", label: "Cancelled", type: "utc"},
  {prop: "notes", label: "Notes", type: "string"},
  {prop: "totalQuantity", label: "Total Quantity", type: "number"},
  {prop: "serialsPending", label: "Serials Missing", type: "number"},
]);

const SUMMARY_FIELDS = fromJS([
  {field: "sku", title: "SKU"},
  {field: "quantity", title: "Qty"},
]);

function addressText(address) {
  if (!address || !address.get("label")) return "";
  return [
    address.get("label"),
    address.get("name"),
    address.get("addressLine1"),
    address.get("addressLine2"),
    [address.get("postcode"), address.get("city")].filter((part) => part).join(" "),
    address.get("stateProvince"),
    address.get("countryCode"),
  ].filter((part) => part).join(", ");
}

export const ClientDispatchInfoPanel = ({clientDispatchId, expanded}) => (
  <PanelGroupController title="Dispatch Info" expanded={expanded !== false}>
    {withDispatch(clientDispatchId, ({loading, error}, data) => {
      if (loading && !data) return <Loading />
      if (error) return <GraphQLErrors error={error} />
      if (!data) return <p className="u-no-selection">Dispatch not found</p>

      const dispatch = fromJS(data);
      const rows = skuSummaryRows(dispatch.get("lines", List()));
      const view = dispatch
        .set("statusLabel", statusLabel(dispatch.get("status")))
        .set("deliverTo", addressText(dispatch.get("deliveryAddress")));
      const waiting = waitingFor(dispatch);
      const unaccepted = dispatch.get("unacceptedSerials", List());

      return (
        <React.Fragment>
          {waiting && <p>{waiting}.</p>}
          <PropertiesList properties={PROPERTIES} data={view} />
          {unaccepted.size > 0 &&
            <React.Fragment>
              <br />
              <p>Stord reported serial numbers that could not be put on this dispatch:</p>
              <ul>
                {unaccepted.map((problem) => <li key={problem.get("key")}>{problem.get("message")}</li>)}
              </ul>
            </React.Fragment>}
          {rows.size > 0 &&
            <React.Fragment>
              <br />
              <InlineTable data={rows} itemKey="sku" fields={SUMMARY_FIELDS} dataType="sku_summary" />
            </React.Fragment>}
        </React.Fragment>
      )
    })}
  </PanelGroupController>
);

const ActionsPanel = (props) => {
  const id = props.clientDispatchId;
  return withDispatch(id, (_result, dispatch) => {
    const isDraft = !!dispatch && dispatch.status === "draft";
    const isOpen = !!dispatch && (dispatch.status === "draft" || dispatch.status === "shipped");
    const missingSerials = isOpen && dispatch.serialsPending > 0;
    const withStord = !!dispatch && !!dispatch.stordOrderNumber;
    return (
      <PanelGroupController title="Actions">
        <ActionButton label="Edit" active={isDraft} onClick={() => props.requestEditClientDispatch(id)} />
        <ActionButton label="Add Pallet" active={isDraft} onClick={() => props.requestAddPalletToClientDispatch(id)} />
        <ActionButton label="Add Ad-hoc Pallet" active={isDraft} onClick={() => props.requestAddManualPalletToClientDispatch(id)} />
        <ActionButton label="Add Serial Numbers" active={missingSerials} onClick={() => props.requestAddSerialsToClientDispatch(id)} />
        {withStord &&
          <ActionButton label="Check Stord Now" active={isDraft || missingSerials} onClick={() => props.requestSyncClientDispatchWithStord(id)} />}
        <ActionButton label="Ship" active={isDraft} onClick={() => props.requestShipClientDispatch(id)} />
        <ActionButton label="Cancel Dispatch" active={isDraft} onClick={() => props.requestCancelClientDispatch(id)} />
      </PanelGroupController>
    )
  });
}

export const ClientDispatchActionsPanel = connect(null, {
  requestEditClientDispatch,
  requestAddPalletToClientDispatch,
  requestAddManualPalletToClientDispatch,
  requestShipClientDispatch,
  requestAddSerialsToClientDispatch,
  requestSyncClientDispatchWithStord,
  requestCancelClientDispatch,
})(ActionsPanel);
